<script setup>
import { onMounted, watch, computed, ref } from 'vue';
import { useI18n } from 'vue-i18n';
import { useStore } from 'dashboard/composables/store';

import KanbanBoard from 'dashboard/components-next/Kanban/KanbanBoard.vue';
import KanbanFilters from 'dashboard/components-next/Kanban/KanbanFilters.vue';
import KanbanOverview from 'dashboard/components-next/Kanban/KanbanOverview.vue';
import KanbanTodayFollowUps from 'dashboard/components-next/Kanban/KanbanTodayFollowUps.vue';
import { useKanbanBoard } from 'dashboard/components-next/Kanban/composables/useKanbanBoard.js';

const { t } = useI18n();
const store = useStore();

const {
  STAGES,
  columns,
  filters,
  sortBy,
  isSearching,
  cardsForStage,
  fetchAllColumns,
  loadMore,
  runSearch,
  moveCard,
} = useKanbanBoard();

const kanbanBoardRef = ref(null);

const isFiltered = computed(() =>
  Boolean(
    filters.agent ||
      filters.verificationStatus ||
      filters.priority ||
      filters.channel ||
      filters.company
  )
);

const handleMove = ({ contact, fromStage, toStage }) => {
  moveCard(contact, fromStage, toStage);
};

const handleLoadMore = stage => loadMore(stage);

const handleSearch = term => runSearch(term);

const handleClear = () => {
  runSearch('');
  fetchAllColumns();
};

const handleSelectStage = stage => {
  kanbanBoardRef.value?.scrollToStage(stage);
};

watch(
  () => [filters.verificationStatus, filters.company, sortBy.value],
  () => {
    if (!isSearching.value) fetchAllColumns();
  }
);

onMounted(() => {
  store.dispatch('agents/get');
  fetchAllColumns();
});
</script>

<template>
  <div class="flex flex-col w-full h-full min-h-0 px-4 py-3">
    <div class="flex items-center justify-between flex-shrink-0 px-1 pb-2">
      <h1 class="text-lg font-medium text-n-slate-12">
        {{ t('KANBAN.TITLE') }}
      </h1>
    </div>
    <KanbanFilters
      v-model:agent="filters.agent"
      v-model:verification-status="filters.verificationStatus"
      v-model:priority="filters.priority"
      v-model:channel="filters.channel"
      v-model:company="filters.company"
      v-model:sort-by="sortBy"
      @search="handleSearch"
      @clear="handleClear"
    />
    <KanbanTodayFollowUps :columns="columns" />
    <KanbanOverview :columns="columns" @select-stage="handleSelectStage" />
    <KanbanBoard
      ref="kanbanBoardRef"
      class="flex-1 min-h-0"
      :stages="STAGES"
      :columns="columns"
      :cards-for-stage="cardsForStage"
      :is-filtered="isFiltered"
      :is-searching="isSearching"
      @move="handleMove"
      @load-more="handleLoadMore"
    />
  </div>
</template>
